import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Stack,
  Skeleton,
} from "@mui/material";
import { motion } from "framer-motion";
import { CheckCircle, Star } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { getPlans } from "../../services/subscription";

const MotionCard = motion(Card);

interface DisplayPlan {
  id: string;
  name: string;
  description: string;
  price: string;
  period: string;
  highlighted: boolean;
  badge?: string;
  features: string[];
  cta: string;
  ctaVariant: "outlined" | "contained";
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
    },
  },
};

const PricingSection: React.FC = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<DisplayPlan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const backendPlans = await getPlans();
        
        // Converter planos do backend para formato de exibição
        const convertedPlans: DisplayPlan[] = backendPlans.map((plan, index) => {
          const isHighlighted = index === Math.floor(backendPlans.length / 2); // Plano do meio é destacado
          
          const formattedPrice = new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(plan.installmentPriceBRL);

          return {
            id: plan.id,
            name: plan.name,
            description: plan.description || `Ideal para empresas com até ${plan.maxUsers} usuários`,
            price: formattedPrice,
            period: plan.installments > 1 ? `/mês (${plan.installments}x)` : "/mês",
            highlighted: isHighlighted,
            badge: isHighlighted ? "Mais Popular" : undefined,
            features: plan.features,
            cta: "Assinar Agora",
            ctaVariant: isHighlighted ? "contained" : "outlined",
          };
        });

        setPlans(convertedPlans);
      } catch (error) {
        console.error("Erro ao carregar planos:", error);
        setPlans([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const handleCtaClick = (plan: DisplayPlan) => {
    // Redireciona para página de login com aba de cadastro e o plano selecionado
    navigate(`/tw/login?tab=cadastro&plan=${plan.id}`);
  };

  return (
    <Box
      id="pricing"
      sx={{
        py: { xs: 10, md: 14 },
        backgroundColor: "#F8FAFC",
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: "#042E4B",
                mb: 2,
                fontSize: { xs: "2rem", md: "2.75rem" },
              }}
            >
              Planos que{" "}
              <Box component="span" sx={{ color: "#EF443C" }}>
                cabem no seu bolso
              </Box>
            </Typography>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "#64748B",
                fontWeight: 400,
                maxWidth: 600,
                mx: "auto",
              }}
            >
              Escolha o plano ideal para sua empresa e comece a transformar seu
              planejamento estratégico hoje mesmo.
            </Typography>
          </motion.div>
        </Box>

        {/* Pricing Cards */}
        {loading ? (
          <Grid container spacing={4} justifyContent="center" alignItems="stretch">
            {[1, 2, 3].map((i) => (
              <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={i}>
                <Card sx={{ height: 480, p: 4, borderRadius: 3 }}>
                  <Skeleton variant="text" width="60%" height={32} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="100%" height={20} sx={{ mb: 3 }} />
                  <Skeleton variant="text" width="40%" height={48} sx={{ mb: 3 }} />
                  <Stack spacing={1.5}>
                    {[1, 2, 3, 4, 5].map((j) => (
                      <Skeleton key={j} variant="text" width="90%" height={24} />
                    ))}
                  </Stack>
                  <Skeleton variant="rectangular" height={48} sx={{ mt: 3, borderRadius: 1 }} />
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          <Grid container spacing={4} justifyContent="center" alignItems="stretch">
            {plans.map((plan) => (
              <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={plan.name}>
                <MotionCard
                  variants={itemVariants}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 3,
                    border: plan.highlighted
                      ? "2px solid #EF443C"
                      : "1px solid #E2E8F0",
                    boxShadow: plan.highlighted
                      ? "0 20px 40px rgba(239,68,60,0.15)"
                      : "0 4px 20px rgba(0,0,0,0.05)",
                    position: "relative",
                    overflow: "visible",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: plan.highlighted
                        ? "0 28px 50px rgba(239,68,60,0.2)"
                        : "0 20px 40px rgba(0,0,0,0.1)",
                    },
                  }}
                >
                  {/* Badge */}
                  {plan.badge && (
                    <Chip
                      icon={<Star sx={{ color: "#F59E0B !important", fontSize: 18 }} />}
                      label={plan.badge}
                      sx={{
                        position: "absolute",
                        top: -16,
                        left: "50%",
                        transform: "translateX(-50%)",
                        backgroundColor: "#042E4B",
                        color: "white",
                        fontWeight: 600,
                        px: 1,
                        "& .MuiChip-label": {
                          px: 1,
                        },
                      }}
                    />
                  )}

                  <CardContent
                    sx={{
                      p: 4,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    {/* Plan Header */}
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          color: "#042E4B",
                          mb: 1,
                        }}
                      >
                        {plan.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#64748B",
                          minHeight: 40,
                        }}
                      >
                        {plan.description}
                      </Typography>
                    </Box>

                    {/* Price */}
                    <Box sx={{ mb: 3 }}>
                      <Stack direction="row" alignItems="baseline" spacing={0.5}>
                        <Typography
                          variant="h3"
                          sx={{
                            fontWeight: 800,
                            color: plan.highlighted ? "#EF443C" : "#042E4B",
                          }}
                        >
                          {plan.price}
                        </Typography>
                        {plan.period && (
                          <Typography
                            variant="body1"
                            sx={{ color: "#64748B" }}
                          >
                            {plan.period}
                          </Typography>
                        )}
                      </Stack>
                    </Box>

                    {/* Features */}
                    <List sx={{ py: 0, mb: 3, flexGrow: 1 }}>
                      {plan.features.map((feature) => (
                        <ListItem
                          key={feature}
                          sx={{ px: 0, py: 0.75 }}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircle
                              sx={{
                                color: plan.highlighted
                                  ? "#EF443C"
                                  : "#10B981",
                                fontSize: 20,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={feature}
                            slotProps={{
                              primary: {
                                variant: "body2",
                                color: "#334155",
                              },
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>

                    {/* CTA Button */}
                    <Button
                      fullWidth
                      size="large"
                      variant={plan.ctaVariant}
                      onClick={() => handleCtaClick(plan)}
                      sx={{
                        py: 1.5,
                        fontWeight: 600,
                        ...(plan.highlighted
                          ? {
                              background:
                                "linear-gradient(135deg, #EF443C 0%, #ff6b5b 100%)",
                              boxShadow: "0 8px 24px rgba(239,68,60,0.3)",
                              "&:hover": {
                                background:
                                  "linear-gradient(135deg, #d63d36 0%, #EF443C 100%)",
                                boxShadow: "0 12px 32px rgba(239,68,60,0.4)",
                              },
                            }
                          : {
                              borderColor: "#042E4B",
                              color: "#042E4B",
                              "&:hover": {
                                backgroundColor: "#042E4B",
                                color: "white",
                              },
                            }),
                      }}
                    >
                      {plan.cta}
                    </Button>
                  </CardContent>
                </MotionCard>
              </Grid>
            ))}
          </Grid>
        </motion.div>
        )}

        {/* Trust Note */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <Box
            sx={{
              textAlign: "center",
              mt: 6,
              p: 3,
              backgroundColor: "white",
              borderRadius: 2,
              border: "1px solid #E2E8F0",
            }}
          >
            <Typography variant="body2" sx={{ color: "#64748B" }}>
              💳 Pagamento seguro via PIX ou cartão de crédito • 🔒 Seus dados
              estão protegidos • ❌ Cancele quando quiser
            </Typography>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default PricingSection;
